import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, Box, Button, Chip, Divider, Modal, TextField, Typography } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import propTypes from "prop-types";

import { apple_pay, mada, tabby, tamara, visa_master } from "../../assets";
import { useBranding, useDir } from "../../hooks";
import { getLocalizedApiMessage, getVoucherMessage } from "../../utils/purchaseHelpers";
import Price from "../Price";
import ThemedButton from "../ThemedButton";
import ModalCloseIcon from "./ModalCloseIcon";

const allowedPaymentMethods = ["visa_master", "tabby", "mada", "tamara", "apple_pay"];

const PurchaseModal = ({
  isOpen,
  isPending,
  onClose,
  onProceed,
  onPaymentMethodChange,
  onPackageStartDateChange,
  paymentMethod,
  data,
  children,
  apiClient,
  memberId,
  appliedVoucher,
  onAppliedVoucherChange,
}) => {
  const dir = useDir();
  const { t, i18n } = useTranslation();
  const branding = useBranding();
  const minDate = data.minStartDate;
  const maxDate = data.maxStartDate;
  const [startDate, setStartDate] = useState(data.startDate);
  const [open, setOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState(appliedVoucher?.code || "");
  const [voucherPreview, setVoucherPreview] = useState(appliedVoucher || null);
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [isApplyingVoucher, setIsApplyingVoucher] = useState(false);
  const [isSuggestionDismissed, setIsSuggestionDismissed] = useState(false);
  const voucherInputRef = useRef(null);
  const validationRequestRef = useRef(0);
  const validationAbortRef = useRef(null);
  const voucherSuggestion = data.voucherSuggestion?.voucherCode || "";
  const voucherAppliedMessage =
    voucherStatus?.type === "success"
      ? voucherStatus.message
      : appliedVoucher
        ? t("packagesModule.voucher.applied")
        : "";
  const sectionStyles = {
    padding: 2,
    border: "1px solid",
    borderColor: "customColors.border",
    borderRadius: 2,
    backgroundColor: "#fff",
  };

  useEffect(() => {
    setVoucherPreview(appliedVoucher || null);
  }, [appliedVoucher]);

  useEffect(() => {
    setVoucherCode("");
    setVoucherPreview(null);
    setVoucherStatus(null);
    setIsSuggestionDismissed(false);
  }, [data.paymentPlanId, memberId]);

  useEffect(() => {
    validationRequestRef.current += 1;
    validationAbortRef.current?.abort();
    setIsApplyingVoucher(false);

    return () => validationAbortRef.current?.abort();
  }, [data.paymentPlanId, memberId, startDate]);

  const applyVoucher = async (code = voucherCode) => {
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setVoucherStatus({ type: "error", message: t("packagesModule.voucher.enterCode") });
      return;
    }
    if (!data.paymentPlanId) {
      setVoucherStatus({ type: "error", message: t("packagesModule.voucher.selectPackage") });
      return;
    }

    validationAbortRef.current?.abort();
    const controller = new AbortController();
    validationAbortRef.current = controller;
    const requestId = validationRequestRef.current + 1;
    validationRequestRef.current = requestId;
    const validationContext = `${memberId}:${data.paymentPlanId}:${startDate}`;

    setIsApplyingVoucher(true);
    setVoucherStatus(null);
    try {
      const response = await apiClient.post(
        "/vouchers/validate",
        {
          memberId,
          paymentPlanId: data.paymentPlanId,
          startDate,
          voucherCode: trimmedCode,
        },
        { signal: controller.signal },
      );
      const currentContext = `${memberId}:${data.paymentPlanId}:${startDate}`;

      if (
        requestId !== validationRequestRef.current ||
        validationContext !== currentContext ||
        controller.signal.aborted
      ) {
        return;
      }

      if (!response.data?.valid) {
        onAppliedVoucherChange(null);
        setVoucherPreview(null);
        setVoucherStatus({
          type: "error",
          message: getLocalizedApiMessage(
            response,
            i18n.language,
            t("packagesModule.voucher.invalid"),
          ),
        });
        return;
      }

      const originalAmount = Number(response.data.originalAmount ?? data.price);
      const discountAmount = Number(response.data.discountAmount ?? 0);
      const discountedAmount = Number(response.data.discountedAmount ?? data.price);
      const preview = {
        code: response.data.voucherCode || trimmedCode,
        originalAmount: Number.isFinite(originalAmount) ? originalAmount : data.price,
        discountAmount: Number.isFinite(discountAmount) ? discountAmount : 0,
        discountedAmount: Number.isFinite(discountedAmount) ? discountedAmount : data.price,
        currency: response.data.currency || "SAR",
        message: getVoucherMessage(response, i18n.language),
      };
      setVoucherCode(preview.code);
      setVoucherPreview(preview);
      onAppliedVoucherChange(preview);
      setVoucherStatus({
        type: "success",
        message: getLocalizedApiMessage(
          response,
          i18n.language,
          t("packagesModule.voucher.applied"),
        ),
      });
    } catch (error) {
      if (
        controller.signal.aborted ||
        requestId !== validationRequestRef.current ||
        error.code === "ERR_CANCELED"
      ) {
        return;
      }

      onAppliedVoucherChange(null);
      setVoucherPreview(null);
      setVoucherStatus({
        type: "error",
        message: error.response
          ? getLocalizedApiMessage(
              error.response,
              i18n.language,
              t("packagesModule.voucher.invalid"),
            )
          : t("packagesModule.voucher.unavailable"),
      });
    } finally {
      if (requestId === validationRequestRef.current) setIsApplyingVoucher(false);
    }
  };

  const removeVoucher = () => {
    setVoucherCode("");
    setVoucherPreview(null);
    setVoucherStatus(null);
    onAppliedVoucherChange(null);
  };

  useEffect(() => {
    new window.TabbyCard({
      selector: "#tabbyCard", // empty div for TabbyCard
      currency: "SAR",
      lang: i18n.language,
      price: data.price,
      size: "narrow", // can be wide
      theme: "default",
      header: false,
    });
  }, [data.price, i18n.language]);

  const selectPaymentMethod = (id) => {
    if (onPaymentMethodChange) onPaymentMethodChange(id);
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const getPaymentMethodLogo = (id) => {
    switch (id) {
      case "visa_master":
        return visa_master;
      case "tabby":
        return tabby;
      case "mada":
        return mada;
      case "tamara":
        return tamara;
      case "apple_pay":
        return apple_pay;
      default:
        return "";
    }
  };

  const handleProceed = () => {
    if (!paymentMethod) return;

    if (voucherCode.trim() && !appliedVoucher?.code) {
      setVoucherStatus({ type: "error", message: t("packagesModule.voucher.reapplyOrRemove") });
      return;
    }

    if (onProceed) onProceed({ selectedPaymentMethod: paymentMethod });
  };

  return (
    <Modal keepMounted open={isOpen} onClose={handleClose} aria-labelledby="purchase-modal-title">
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          width: { xs: "calc(100% - 24px)", sm: "92%", md: "min(920px, 94vw)" },
          maxHeight: "90vh",
          top: "50%",
          left: "50%",
          p: 0,
          position: "absolute",
          transform: "translate(-50%, -50%)",
          backgroundColor: "customColors.modalBg",
          borderRadius: 2.5,
          boxShadow: "0 24px 80px rgba(0, 0, 0, 0.28)",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "20px",
            backgroundColor: "#fff",
            width: "42%",
            minHeight: 580,
            borderInlineEnd: "1px solid",
            borderColor: "customColors.border",
          }}
        >
          {branding && (
            <Box
              component="img"
              src={branding.assets?.darkLogo}
              alt={branding.logoAltText}
              width={100}
              height="auto"
              sx={{
                marginTop: 3,
                marginInlineStart: 3,
              }}
            />
          )}
          {branding && (
            <Box
              component="img"
              src={branding.assets?.identityGraphic}
              width="88%"
              height="auto"
              sx={{ marginTop: 5, transform: dir === "rtl" ? "scaleX(-1)" : "" }}
              alt="v shaped style svg"
            />
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: "customColors.modalBg",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            p: { xs: 2, sm: 2.5, md: 3 },
            width: { xs: "100%", md: "58%" },
          }}
        >
          <ModalCloseIcon onClose={handleClose} />
          <Box>
            <Typography
              variant="h1"
              component="h2"
              id="purchase-modal-title"
              fontWeight="fontWeightBold"
              sx={{ paddingInlineEnd: 4, color: "primary.main", lineHeight: 1.25 }}
            >
              {data.name[i18n.language]}
            </Typography>
            {data.membershipType && (
              <Box
                sx={{
                  marginTop: 1.25,
                  padding: 1.25,
                  borderRadius: 1.5,
                  backgroundColor: "primary.light",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0.75,
                }}
              >
                <Chip
                  size="small"
                  color="primary"
                  label={data.membershipType.label}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "& .MuiChip-label": { paddingInline: 1.5 },
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {data.membershipType.description}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ ...sectionStyles, padding: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ marginBottom: 0.75 }}>
              {t("startDate")}
            </Typography>
            {data.enableCustomStartDate ? (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("pickStartDate")} : {t("maxAllowedStartDate")}{" "}
                  {new Date(maxDate).toLocaleDateString(
                    i18n.language === "en" ? "en-US" : "ar-EG",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    },
                  )}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label=""
                    sx={{ marginTop: 1 }}
                    value={data.startDate}
                    onChange={(newValue) => {
                      if (newValue && !isNaN(new Date(newValue))) {
                        const sd = format(new Date(newValue), "yyyy-MM-dd");
                        setStartDate(sd);
                        if (appliedVoucher && sd !== startDate) {
                          onAppliedVoucherChange(null);
                          setVoucherPreview(null);
                          setVoucherStatus({
                            type: "error",
                            message: t("packagesModule.voucher.startDateChanged"),
                          });
                        }
                        onPackageStartDateChange(sd);
                        setOpen(false);
                      } else {
                        onPackageStartDateChange(startDate);
                        setOpen(false);
                      }
                    }}
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    onAccept={() => setOpen(false)}
                    minDate={minDate}
                    maxDate={maxDate}
                    format="dd/MM/yyyy"
                    disablePast
                    slotProps={{
                      textField: {
                        onKeyDown: (e) => e.preventDefault(),
                        onPaste: (e) => e.preventDefault(),
                        onDrop: (e) => e.preventDefault(),
                        // onFocus: () => setOpen(true),
                        onClick: () => setOpen(true),
                        inputProps: { readOnly: true },
                      },
                      openPickerButton: {
                        onMouseDown: (e) => {
                          e.preventDefault();
                          setOpen(true);
                        },
                      },
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
                <Box sx={{ marginTop: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {new Date(startDate).toLocaleDateString(
                      i18n.language === "en" ? "en-US" : "ar-EG",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" fontWeight={600}>
                {new Date(data.startDate).toLocaleDateString(
                  i18n.language === "en" ? "en-US" : "ar-EG",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </Typography>
            )}
          </Box>
          <Box sx={sectionStyles}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ marginBottom: 1 }}>
              {t("packagesModule.voucher.code")}
            </Typography>
            {voucherSuggestion &&
              !appliedVoucher &&
              !isSuggestionDismissed &&
              voucherCode !== voucherSuggestion && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                    marginBottom: 1.25,
                    padding: 1,
                    borderRadius: 1.5,
                    backgroundColor: "primary.light",
                  }}
                >
                  <Typography variant="caption">
                    {t("packagesModule.voucher.suggestion")}
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    aria-label={t("packagesModule.voucher.suggestionAction", {
                      code: voucherSuggestion,
                    })}
                    onClick={() => {
                      setVoucherCode(voucherSuggestion);
                      setVoucherStatus(null);
                      applyVoucher(voucherSuggestion);
                    }}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      backgroundColor: "#fff",
                      paddingBlock: 0.5,
                    }}
                  >
                    {voucherSuggestion}
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    color="inherit"
                    aria-label={t("packagesModule.voucher.dismissSuggestion")}
                    onClick={() => setIsSuggestionDismissed(true)}
                    sx={{ minWidth: 32 }}
                  >
                    ×
                  </Button>
                </Box>
              )}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                alignItems: "stretch",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label={t("packagesModule.voucher.code")}
                value={voucherCode}
                disabled={isApplyingVoucher}
                inputRef={voucherInputRef}
                onChange={(event) => {
                  if (appliedVoucher) {
                    onAppliedVoucherChange(null);
                    setVoucherPreview(null);
                  }
                  setVoucherCode(event.target.value);
                  setVoucherStatus(null);
                }}
              />
              <ThemedButton
                variant="outlined"
                disabled={isApplyingVoucher}
                onClick={appliedVoucher ? removeVoucher : () => applyVoucher()}
                sx={{ whiteSpace: "nowrap", minWidth: 100 }}
              >
                {appliedVoucher
                  ? t("packagesModule.voucher.remove")
                  : isApplyingVoucher
                    ? t("packagesModule.voucher.applying")
                    : t("packagesModule.voucher.apply")}
              </ThemedButton>
            </Box>
            {voucherAppliedMessage && (
              <Box
                role="status"
                aria-live="polite"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  marginTop: 1.25,
                  color: "success.main",
                  overflow: "visible",
                }}
              >
                <Typography component="span" aria-hidden="true" sx={{ lineHeight: 1 }}>
                  ✓
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}
                >
                  {voucherAppliedMessage}
                </Typography>
              </Box>
            )}
            {voucherStatus?.type === "error" && (
              <Alert
                severity="error"
                aria-live="assertive"
                sx={{
                  marginTop: 1.25,
                  alignItems: "center",
                  "& .MuiAlert-message": {
                    width: "100%",
                    minWidth: 0,
                    paddingBlock: 0.25,
                    overflow: "visible",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                  },
                }}
              >
                {voucherStatus.message}
              </Alert>
            )}
          </Box>
          <Box sx={sectionStyles}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ marginBottom: 1.25 }}>
              {t("paymentMethod")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "start", gap: 1 }}>
              {data.paymentMethods.map((item) => (
                <PaymentMethodButton
                  key={item}
                  id={item}
                  logo={getPaymentMethodLogo(item)}
                  isSelected={item === paymentMethod}
                  onSelect={() => selectPaymentMethod(item)}
                />
              ))}
            </Box>
          </Box>
          <Box
            component="div"
            sx={{
              display: paymentMethod === "tabby" ? "block" : "none",
              width: "100%",
              overflow: "auto",
              marginTop: 1,
              height: "200px",
              "& .styles__tabby-card--d5b39": {
                paddingBottom: 0,
              },
            }}
            id="tabbyCard"
          ></Box>
          <Box sx={{ ...sectionStyles, backgroundColor: "customColors.bg2" }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "text.secondary", marginBottom: 1 }}
            >
              {t("total")}
            </Typography>
            {voucherPreview ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    marginBottom: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("packagesModule.voucher.originalPrice")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {voucherPreview.originalAmount} {voucherPreview.currency}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" color="success.main">
                    {t("packagesModule.voucher.discount")}
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight={700}>
                    -{voucherPreview.discountAmount} {voucherPreview.currency}
                  </Typography>
                </Box>
                {voucherPreview.message && (
                  <Typography
                    variant="body2"
                    sx={{
                      marginTop: 1,
                      padding: 1,
                      borderRadius: 1,
                      backgroundColor: "success.light",
                      color: "text.secondary",
                    }}
                  >
                    {voucherPreview.message}
                  </Typography>
                )}
                <Divider sx={{ marginBlock: 1 }} />
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography variant="body1" fontWeight={700}>
                    {t("total")}
                  </Typography>
                  <Price value={voucherPreview.discountedAmount} compact />
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" fontWeight={700}>
                  {t("total")}
                </Typography>
                <Price value={data.price} compact />
              </Box>
            )}
          </Box>
          {paymentMethod === "apple_pay" ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginTop: 2,
              }}
              component="div"
            >
              {isPending && (
                <Typography sx={{ marginBottom: 1 }} variant="body2" color="primary">
                  {t("clickButtonBelow")}
                </Typography>
              )}
              <form
                action={
                  branding?.publicUrl
                    ? `${branding.publicUrl}/order-complete?applepayment=true`
                    : ""
                }
                className="paymentWidgets"
                data-brands="APPLEPAY"
                style={{ direction: "ltr" }}
              ></form>
            </Box>
          ) : null}
          <ThemedButton
            variant="contained"
            fullWidth
            disabled={paymentMethod === null}
            loading={isPending}
            onClick={handleProceed}
            sx={{
              marginTop: 0.5,
              minHeight: 48,
              fontSize: "1rem",
              ...(paymentMethod === "apple_pay" && isPending ? { display: "none" } : {}),
            }}
          >
            {t("proceed")}
          </ThemedButton>
        </Box>
        {children}
      </Box>
    </Modal>
  );
};

PurchaseModal.propTypes = {
  isOpen: propTypes.bool.isRequired,
  isPending: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
  onProceed: propTypes.func.isRequired,
  onPackageStartDateChange: propTypes.func.isRequired,
  onPaymentMethodChange: propTypes.func.isRequired,
  paymentMethod: propTypes.oneOf([...allowedPaymentMethods, null]),
  data: propTypes.shape({
    name: propTypes.object.isRequired,
    price: propTypes.number.isRequired,
    startDate: propTypes.string,
    paymentMethods: propTypes.arrayOf(propTypes.oneOf(allowedPaymentMethods)).isRequired,
    paymentFormUrl: propTypes.string,
    maxStartDate: propTypes.string,
    minStartDate: propTypes.string,
    enableCustomStartDate: propTypes.bool,
    paymentPlanId: propTypes.oneOfType([propTypes.number, propTypes.string]),
    voucherSuggestion: propTypes.shape({
      hasSuggestion: propTypes.bool.isRequired,
      voucherCode: propTypes.string.isRequired,
      source: propTypes.oneOf(["plan_custom_attribute"]).isRequired,
    }),
    membershipType: propTypes.shape({
      label: propTypes.string.isRequired,
      description: propTypes.string.isRequired,
    }),
  }).isRequired,
  children: propTypes.node,
  apiClient: propTypes.object.isRequired,
  memberId: propTypes.oneOfType([propTypes.number, propTypes.string]).isRequired,
  appliedVoucher: propTypes.object,
  onAppliedVoucherChange: propTypes.func.isRequired,
};

export default PurchaseModal;

const PaymentMethodButton = ({ id, logo, isSelected, onSelect }) => {
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={id.replace("_", " ")}
      sx={{
        minWidth: { xs: "72px", sm: "84px" },
        height: 42,
        padding: 0.75,
        border: isSelected ? "2px solid" : "1px solid",
        borderColor: isSelected ? "primary.main" : "customColors.border",
        borderRadius: 1.5,
        backgroundColor: isSelected ? "primary.light" : "#fff",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        transition: "border-color 150ms ease, background-color 150ms ease, transform 150ms ease",
        transform: isSelected ? "translateY(-1px)" : "none",
        "&:hover": {
          borderColor: "primary.main",
          backgroundColor: "primary.light",
        },
        "&:focus-visible": {
          outline: "3px solid",
          outlineColor: "primary.light",
          outlineOffset: 2,
        },
      }}
    >
      <Box
        component="img"
        src={logo}
        alt={id}
        sx={{
          width: "auto",
          height: "auto",
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
        }}
      />
    </Box>
  );
};

PaymentMethodButton.propTypes = {
  id: propTypes.string.isRequired,
  logo: propTypes.string.isRequired,
  isSelected: propTypes.bool.isRequired,
  onSelect: propTypes.func.isRequired,
};
